// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type AssetsInfoResp } from '@shared/data-access/interfaces/api/ai/assets';
import { type BrokerPosition } from '@shared/data-access/interfaces/api/brokerage/portfolio';
import {
  type MyInvestments,
  type SubRows,
} from '@shared/data-access/interfaces/common/my-investment';
import { assetsApi } from '@shared/data-access/api/ai/assets-api';
import type { AppDispatch } from '@advisor-app/store';
import { portfolioApi } from '@advisor-app/store/api/brokerage/portfolio-api';
import {
  groupPositionsByAssetType,
  type GroupedPositions,
  type InvestmentAssetType,
} from '@shared/data-access/transformers/assets/group-positions-by-asset-type';

type AssetInvestment = Record<InvestmentAssetType, MyInvestments>;

export const buildInvestmentTable = async (broker: any, dispatch: AppDispatch) => {
  const fetchPortfolioPositions = async () => {
    const { accountTypeSelect, brokerConnected, account } = broker;
    const params = {
      broker_key: brokerConnected,
      type_account: accountTypeSelect,
    };
    const payload = {
      account_id: account.account_id,
    };

    return dispatch(
      portfolioApi.endpoints.getPositions.initiate({
        params,
        payload,
      }),
    ).unwrap();
  };

  const classifyPositions = async (positions: BrokerPosition[]) => {
    const tickers = positions.map(position => position.ticker);
    const action = assetsApi.endpoints.classifier.initiate({ payload: tickers });
    const response = await dispatch(action).unwrap();
    return groupPositionsByAssetType(response);
  };

  const fetchAssetsInfo = async (positions: GroupedPositions) => {
    const requests = Object.entries(positions)
      .map(([assetType, assets]) => {
        if (assets.length === 0 || assetType === 'other') return null;
        const action = assetsApi.endpoints.assetsInfo.initiate({
          payload: assets.map(({ ticker }) => ticker),
          params: { detail: true },
          assetType: assetType as InvestmentAssetType,
        });
        return dispatch(action).unwrap();
      })
      .filter(Boolean) as Promise<AssetsInfoResp[]>[];
    const response = await Promise.all(requests);
    return response.flat();
  };

  const buildTable = (
    portfolio: BrokerPosition[],
    assetsInfo: AssetsInfoResp[],
    classifiedPositions: GroupedPositions,
  ) => {
    const dataTable = {} as AssetInvestment;

    const summary = {
      stock: 0,
      etf: 0,
      mutual_fund: 0,
      other: 0,
    };

    for (const [assetClass, positions] of Object.entries(classifiedPositions)) {
      let stocksQuantity = 0;
      let totalUnrealizedCapitalGains = 0;
      const subRows = positions
        .map(item => {
          const assetInfo = assetsInfo.find(
            ({ ticker }) => ticker === item.ticker,
          ) as any;

          const position = portfolio.find(
            ({ ticker }) => ticker === item.ticker,
          ) as any;

          if (!position) return null;

          summary[assetClass as keyof typeof summary] +=
            position.outstanding_balance;
          stocksQuantity += position.stocks_number;
          totalUnrealizedCapitalGains += position.unrealized_capital_gains;

          const localCurrencyValues = position.local_currency_values;

          return {
            name: assetInfo?.name,
            asset_class: assetClass,
            investment: position.outstanding_balance,
            price: position.outstanding_balance / position.stocks_number,
            ticker: position.ticker,
            quantity: position.stocks_number,
            currency: position.currency,
            local_currency_values: {
              ...localCurrencyValues,
              price:
                localCurrencyValues.outstanding_balance / position.stocks_number,
            },
            total_gain_loss: [position.unrealized_capital_gains, 0],
            url_logo: assetInfo?.url_logo,
            url_morningstar: assetInfo?.url_morningstar || 'missing',
            subRows: [],
            unrealized_capital_gains: position.unrealized_capital_gains,
          };
        })
        .filter(Boolean) as SubRows[];

      dataTable[assetClass as InvestmentAssetType] = {
        subRows,
        quantity: stocksQuantity,
        asset_class: assetClass,
        investment: summary[assetClass as keyof typeof summary],
        price: 0,
        total_gain_loss: [totalUnrealizedCapitalGains, 0],
      };
    }

    return { dataTable, summary };
  };

  const buildInvestments = (dataTable: AssetInvestment) => {
    return Object.entries(dataTable).reduce((acc, [key, value]) => {
      return {
        ...acc,
        [key]: value.subRows,
      };
    }, {} as Record<InvestmentAssetType, SubRows[]>);
  };

  const positions = await fetchPortfolioPositions();
  const classifiedPositions = await classifyPositions(positions);
  const assetsInfo = await fetchAssetsInfo(classifiedPositions);

  const { dataTable, summary } = buildTable(
    positions,
    assetsInfo,
    classifiedPositions,
  );

  const investments = buildInvestments(dataTable);

  const table = Object.values(dataTable).filter(item => item.subRows.length > 0);

  return { investments, summary, dataTable: table };
};
